// frontend/grautomates/src/components/HR/PayslipsSelfService.jsx
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  EyeIcon,
  ArrowDownTrayIcon,
  EnvelopeIcon,
  UserGroupIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import PageLayout from '../shared/PageLayout';
import DatePicker from '../shared/DatePicker';
import { Table, THead, TBody, TR, TH, TD } from '../shared/DataTable';
import Button from '../shared/Button';
import Modal, { ModalFooter } from '../shared/Modal';
import { TEXT } from '../shared/Typography';
import { FIELD_INPUT_CLASS } from '../shared/FormInput';
import Alert from '../shared/Alert';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';

function parsePayDate(item: any): Date | null {
  const candidates = [
    item?.PayDate,
    item?.PaymentDate,
    item?.PaySummaryEndDate,
    item?.PayPeriodEndDate,
  ];
  for (const d of candidates) {
    if (!d) continue;
    const dt = new Date(d);
    if (!isNaN(dt.getTime())) return dt;
  }
  return null;
}
function toISODate(d: Date | null): string {
  if (!d || isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}
function startOfFY(d) {
  const year = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
  return new Date(year, 6, 1);
}
function endOfFY(d) {
  const year = d.getMonth() >= 6 ? d.getFullYear() + 1 : d.getFullYear();
  return new Date(year, 5, 30, 23, 59, 59, 999);
}
function addMonths(d, months) {
  const dd = new Date(d);
  dd.setMonth(dd.getMonth() + months);
  return dd;
}
function uniqKey(it, idx) {
  return (it?.PayNo || 'N') + '_' + idx;
}

export default function PayslipsSelfService() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [raw, setRaw] = useState<any[]>([]);
  // Pagination metadata returned by the cached payslips endpoint. Used to
  // decide whether to render the "Load older" button below the list.
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [toast, setToast] = useState('');
  const [preview, setPreview] = useState<any>(null);

  const [busyEmail, setBusyEmail] = useState('');
  const [busyDownload, setBusyDownload] = useState('');

  // Bulk select
  const [checked, setChecked] = useState<Record<string, any>>({});
  const allChecked = useMemo(
    () => Object.values(checked).length > 0 && Object.values(checked).every(Boolean),
    [checked]
  );
  const anyChecked = useMemo(() => Object.values(checked).some(Boolean), [checked]);

  // Admin access + UI state
  const [canAdmin, setCanAdmin] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);

  // Admin search & results
  const [adminQuery, setAdminQuery] = useState('');
  const [adminSearching, setAdminSearching] = useState(false);
  const [adminResults, setAdminResults] = useState<any[]>([]);
  const [adminSelected, setAdminSelected] = useState<any>(null); // {personCode, name, email}
  const [adminItems, setAdminItems] = useState<any[]>([]);
  const [adminBusyEmail, setAdminBusyEmail] = useState('');
  const [adminBusyDownload, setAdminBusyDownload] = useState('');

  // Check admin capability
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get('/auth/me');
        const roles = [
          ...(r?.data?._computedRoles || []),
          ...((r?.data?.dbUser?.roles || []).map(x => x?.name) || []),
          ...(r?.data?.roles || []),
          r?.data?.role,
        ]
          .filter(Boolean)
          .map(s => String(s).toLowerCase());
        // Viewing OTHER employees' payslips requires the payroll role only —
        // deliberately not platform:admin. Mirrors isPayslipAdmin() in
        // backend/routes/selfPayslips.ts; keep the two in step. Own-payslip
        // self-service below is unaffected and stays open to every user.
        setCanAdmin(roles.includes('hr_payslip:admin'));
      } catch {
        setCanAdmin(false);
      }
    })();
  }, []);

  // Fetch data (self)
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const r = await axios.get('/api/self/payslips', {
          headers: { Accept: 'application/json' },
        });
        const items = Array.isArray(r?.data?.items) ? r.data.items : [];

        if (!mounted) return;
        const normalized = items
          .map(it => {
            const when = parsePayDate(it);
            return {
              ...it,
              _when: when,
              _whenISO: when ? toISODate(when) : '',
              _totalNet: Number(it?.TotalNet ?? it?.TotalPay ?? 0) || 0,
            };
          })
          .filter(it => it._when);

        normalized.sort((a, b) => b._when - a._when);
        setRaw(normalized);
        setHasOlder(!!r?.data?.hasOlder);

        if (normalized.length > 0) {
          const newest = normalized[0]._when;
          const oldest = normalized[normalized.length - 1]._when;
          setFrom(toISODate(oldest));
          setTo(toISODate(newest));
        } else {
          const now = new Date();
          setFrom(toISODate(startOfFY(now)));
          setTo(toISODate(endOfFY(now)));
        }
      } catch (e) {
        setErr(e?.response?.data?.reason || e?.message || 'Failed to load payslips');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Lazy-load: extend the cache one more page back. Backend re-returns the
  // full updated set; we re-normalise and replace `raw`.
  const loadOlder = async () => {
    setLoadingOlder(true);
    setErr('');
    try {
      const r = await axios.get('/api/self/payslips', {
        headers: { Accept: 'application/json' },
        params: { loadOlder: 'true' },
      });
      const items = Array.isArray(r?.data?.items) ? r.data.items : [];
      const normalized = items
        .map((it: any) => {
          const when = parsePayDate(it);
          return {
            ...it,
            _when: when,
            _whenISO: when ? toISODate(when) : '',
            _totalNet: Number(it?.TotalNet ?? it?.TotalPay ?? 0) || 0,
          };
        })
        .filter((it: any) => it._when);
      normalized.sort((a: any, b: any) => b._when - a._when);
      setRaw(normalized);
      setHasOlder(!!r?.data?.hasOlder);
      // Widen the date range to include the newly loaded older pays
      if (normalized.length > 0) {
        const oldest = normalized[normalized.length - 1]._when;
        setFrom(toISODate(oldest));
      }
    } catch (e: any) {
      setErr(e?.response?.data?.reason || e?.message || 'Failed to load older payslips');
    } finally {
      setLoadingOlder(false);
    }
  };

  // Filtered view
  const list = useMemo(() => {
    if (!raw?.length) return [];
    const fromD = from ? new Date(from) : null;
    const toD = to ? new Date(`${to}T23:59:59.999`) : null;
    return raw.filter(it => {
      const t = it._when;
      if (!t) return false;
      if (fromD && t < fromD) return false;
      if (toD && t > toD) return false;
      return true;
    });
  }, [raw, from, to]);

  const newestISO = raw?.[0]?._whenISO || '';
  const oldestISO = raw?.[raw.length - 1]?._whenISO || '';

  // Quick ranges
  const setQuickAll = () => {
    if (raw.length) {
      setFrom(oldestISO);
      setTo(newestISO);
    }
  };
  const setQuickMonths = m => {
    const now = new Date();
    setFrom(toISODate(addMonths(now, -m)));
    setTo(toISODate(now));
  };
  const setQuickThisFY = () => {
    const now = new Date();
    setFrom(toISODate(startOfFY(now)));
    setTo(toISODate(endOfFY(now)));
  };
  const setQuickLastFY = () => {
    const now = new Date();
    const lastFYRef = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    setFrom(toISODate(startOfFY(lastFYRef)));
    setTo(toISODate(endOfFY(lastFYRef)));
  };

  // Self actions
  const doPreview = it => setPreview(it);

  const doDownload = async it => {
    const payNo = String(it?.PayNo || '').trim();
    if (!payNo) {
      setToast('No PayNo on this record.');
      return;
    }
    setBusyDownload(payNo);
    try {
      const url = `/api/self/payslips/${encodeURIComponent(payNo)}/pdf?v=${Date.now()}`;
      const r = await axios.get(url, {
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
      });
      const ct = String(r?.headers?.['content-type'] ?? '');
      if (!ct.includes('pdf')) {
        const text = await r.data.text?.().catch(() => null);
        throw new Error(text || 'Unexpected response (not a PDF).');
      }
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `Payslip_${payNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setToast('PDF downloaded.');
    } catch (e) {
      const msg =
        e?.response?.data?.error === 'unavailable_until_next_day'
          ? 'This payslip will be available from 12:01am tomorrow.'
          : e?.response?.data?.error || e?.message || 'Download failed';
      setToast(`Download failed: ${msg}`);
      console.error('Download error', e);
    } finally {
      setBusyDownload('');
    }
  };

  const doEmail = async it => {
    const payNo = String(it?.PayNo || '').trim();
    if (!payNo) {
      setToast('No PayNo on this record.');
      return;
    }
    setBusyEmail(payNo);
    try {
      const url = `/api/self/payslips/${encodeURIComponent(payNo)}/email`;
      const r = await axios.post(url);
      if (r?.data?.ok) setToast('Payslip emailed to you.');
      else setToast(r?.data?.error ? `Email failed: ${r.data.error}` : 'Email send failed.');
    } catch (e) {
      const msg =
        e?.response?.data?.error === 'unavailable_until_next_day'
          ? 'This payslip will be available from 12:01am tomorrow.'
          : e?.response?.data?.error || e?.message || 'Email failed';
      setToast(`Email failed: ${msg}`);
      console.error('Email error', e);
    } finally {
      setBusyEmail('');
    }
  };

  const toggleAllVisible = () => {
    if (allChecked) {
      setChecked({});
      return;
    }
    const next = {};
    list.forEach((it, idx) => (next[uniqKey(it, idx)] = true));
    setChecked(next);
  };
  const _clearSelection = () => setChecked({});

  const bulkDownload = async () => {
    const selected = list.filter((it, idx) => checked[uniqKey(it, idx)]);
    if (!selected.length) return;
    for (const it of selected) {

      await doDownload(it);
    }
  };
  const bulkEmail = async () => {
    const selected = list.filter((it, idx) => checked[uniqKey(it, idx)]);
    if (!selected.length) return;
    for (const it of selected) {

      await doEmail(it);
    }
  };

  /* ===== Admin: search by code OR name ===== */
  const adminSearch = async () => {
    const q = adminQuery.trim();
    if (!q) return;
    setAdminSearching(true);
    setAdminResults([]);
    setAdminSelected(null);
    setAdminItems([]);
    try {
      const r = await axios.get('/api/self/payslips/admin/search', { params: { q } });
      setAdminResults(Array.isArray(r?.data?.results) ? r.data.results : []);
    } catch (e) {
      setToast(e?.response?.data?.error || e?.message || 'Search failed');
    } finally {
      setAdminSearching(false);
    }
  };

  const adminLoad = async personCode => {
    if (!personCode) return;
    setAdminSelected(null);
    setAdminItems([]);
    try {
      const sel = adminResults.find(x => x.personCode === personCode) || {
        personCode,
        name: 'Person ' + personCode,
      };
      setAdminSelected(sel);
      const r = await axios.get(`/api/self/payslips/admin/${encodeURIComponent(personCode)}`);
      setAdminItems(Array.isArray(r?.data?.items) ? r.data.items : []);
    } catch (e) {
      setToast(e?.response?.data?.error || e?.message || 'Load failed');
    }
  };

  const adminDownload = async (personCode, it) => {
    const payNo = String(it?.PayNo || '').trim();
    if (!payNo) return;
    setAdminBusyDownload(payNo);
    try {
      const url = `/api/self/payslips/admin/${encodeURIComponent(personCode)}/${encodeURIComponent(payNo)}/pdf?v=${Date.now()}`;
      const r = await axios.get(url, {
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
      });
      const ct = String(r?.headers?.['content-type'] ?? '');
      if (!ct.includes('pdf')) {
        const text = await r.data.text?.().catch(() => null);
        throw new Error(text || 'Unexpected response (not a PDF).');
      }
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `Payslip_${payNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setToast('PDF downloaded.');
    } catch (e) {
      const msg =
        e?.response?.data?.error === 'unavailable_until_next_day'
          ? 'This payslip will be available from 12:01am tomorrow.'
          : e?.response?.data?.error || e?.message || 'Download failed';
      setToast(`Download failed: ${msg}`);
    } finally {
      setAdminBusyDownload('');
    }
  };

  const adminEmail = async (personCode, it) => {
    const payNo = String(it?.PayNo || '').trim();
    if (!payNo) return;
    setAdminBusyEmail(payNo);
    try {
      const url = `/api/self/payslips/admin/${encodeURIComponent(personCode)}/${encodeURIComponent(payNo)}/email`;
      const r = await axios.post(url); // optionally provide { to } in body or ?to=email
      if (r?.data?.ok) setToast('Payslip emailed.');
      else setToast(r?.data?.error ? `Email failed: ${r.data.error}` : 'Email send failed.');
    } catch (e) {
      const msg =
        e?.response?.data?.error === 'unavailable_until_next_day'
          ? 'This payslip will be available from 12:01am tomorrow.'
          : e?.response?.data?.error || e?.message || 'Email failed';
      setToast(`Email failed: ${msg}`);
    } finally {
      setAdminBusyEmail('');
    }
  };

  return (
    <PageLayout
      title="Self Service Payslips"
      subtitle="Filter and access your payslips"
      showLogo={false}
      actions={
        canAdmin ? (
          <Button variant="outline" size="sm" onClick={() => setShowAdmin(true)} title="Find staff and act on their payslips" fullWidth className="sm:w-auto">
            <UserGroupIcon className="h-5 w-5" />
            <span>Admin: Find Staff</span>
          </Button>
        ) : undefined
      }
    >
        {/* Filters */}
        <div className="mb-5">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            <div className="flex-1 sm:flex-none">
              <DatePicker
                label="From"
                value={from}
                onChange={setFrom}
                min={oldestISO || undefined}
                max={to || undefined}
              />
            </div>
            <div className="flex-1 sm:flex-none">
              <DatePicker
                label="To"
                value={to}
                onChange={setTo}
                min={from || undefined}
                max={newestISO || undefined}
              />
            </div>
            {/* Quick range buttons */}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="xs" onClick={setQuickAll}>
                All
              </Button>
              <Button variant="outline" size="xs" onClick={() => setQuickMonths(6)}>
                6 mo
              </Button>
              <Button variant="outline" size="xs" onClick={() => setQuickMonths(12)}>
                12 mo
              </Button>
              <Button variant="outline" size="xs" onClick={() => setQuickMonths(24)} className="hidden sm:inline-flex">
                24 mo
              </Button>
              <Button variant="outline" size="xs" onClick={setQuickThisFY}>
                This FY
              </Button>
              <Button variant="outline" size="xs" onClick={setQuickLastFY}>
                Last FY
              </Button>
            </div>
          </div>
        </div>

        {raw.length > 0 && (
          <div className={`mt-3 sm:text-sm ${TEXT.captionMuted}`}>
            Showing <b>{list.length}</b> of <b>{raw.length}</b> payslips
            <span className="hidden sm:inline opacity-80">
              {' '}· Range: {oldestISO || '—'} → {newestISO || '—'}
            </span>
          </div>
        )}

      <hr className="border-gray-100 mb-5" />

      {/* Body */}
      {loading && (
        <LoadingSpinner message="Loading payslips…" className="py-6" />
      )}

      {!loading && err && (
        <Alert
          variant="error"
          title="Couldn’t load payslips"
          message={<span className="break-all">{String(err)}</span>}
        />
      )}

      {!loading && !err && raw.length === 0 && (
        <EmptyState title="No payslips found for your account." />
      )}

      {!loading && !err && raw.length > 0 && list.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className={`mb-2 ${TEXT.strong}`}>No payslips in the selected range</div>
          <div className={`mb-4 ${TEXT.bodyMuted}`}>
            Try widening the dates. Your available data spans <b>{oldestISO}</b> →{' '}
            <b>{newestISO}</b>.
          </div>
          <Button variant="outline" onClick={setQuickAll}>
            Show All Payslips
          </Button>
        </div>
      )}

      {!loading && !err && list.length > 0 && (
        <div className="-mx-4 sm:-mx-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          {/* Bulk toolbar */}
          <div className="p-3 flex flex-wrap items-center gap-2">
            <Button variant="outline" size="xs" onClick={toggleAllVisible}>
              <CheckCircleIcon className="h-5 w-5" />
              <span className="hidden sm:inline">{allChecked ? 'Clear selection' : 'Select all'}</span>
              <span className="sm:hidden">{allChecked ? 'Clear' : 'All'}</span>
            </Button>
            <div className="flex-1" />
            <Button variant="outline" size="xs" onClick={bulkDownload} disabled={!anyChecked}>
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Download Selected</span>
            </Button>
            <Button variant="outline" size="xs" onClick={bulkEmail} disabled={!anyChecked}>
              <EnvelopeIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Email Selected</span>
            </Button>
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block overflow-x-auto">
            <Table scroll={false} className="min-w-full">
              <THead>
                <TR>
                  <TH className="w-10"></TH>
                  <TH>Pay No</TH>
                  <TH>Payment Date</TH>
                  <TH align="right">Total Net</TH>
                  <TH>Pay Period</TH>
                  <TH>Actions</TH>
                </TR>
              </THead>
              <TBody>
                {list.map((it, idx) => {
                  const key = uniqKey(it, idx);
                  const payNo = String(it?.PayNo || '').trim();
                  const emailing = busyEmail === payNo;
                  const downloading = busyDownload === payNo;
                  return (
                    <TR key={key}>
                      <TD className="align-top">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={!!checked[key]}
                          onChange={(e: any) => setChecked(s => ({ ...s, [key]: e.target.checked }))}
                        />
                      </TD>
                      <TD className="font-mono">{it.PayNo || '—'}</TD>
                      <TD>
                        {it.PaymentDate ? new Date(it.PaymentDate).toLocaleDateString() : '—'}
                      </TD>
                      <TD align="right">
                        {it.TotalNet != null ? Number(it.TotalNet).toFixed(2) : '—'}
                      </TD>
                      <TD>
                        {(it.PaySummaryStartDate
                          ? new Date(it.PaySummaryStartDate).toLocaleDateString()
                          : '—') +
                          ' → ' +
                          (it.PaySummaryEndDate
                            ? new Date(it.PaySummaryEndDate).toLocaleDateString()
                            : '—')}
                      </TD>
                      <TD>
                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" size="xs" onClick={() => doPreview(it)} title="Preview PDF">
                            <EyeIcon className="h-5 w-5" /> Preview
                          </Button>
                          <Button variant="outline" size="xs" onClick={() => doDownload(it)} disabled={downloading} title="Download PDF">
                            <ArrowDownTrayIcon
                              className={`h-5 w-5 ${downloading ? 'animate-pulse' : ''}`}
                            />{' '}
                            {downloading ? 'Downloading…' : 'Download'}
                          </Button>
                          <Button variant="outline" size="xs" onClick={() => doEmail(it)} disabled={emailing} title="Email me my payslip">
                            <EnvelopeIcon className={`h-5 w-5 ${emailing ? 'animate-pulse' : ''}`} />{' '}
                            {emailing ? 'Sending…' : 'Email me'}
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </div>

          {/* Mobile card view */}
          <div className="md:hidden divide-y divide-gray-200">
            {list.map((it, idx) => {
              const key = uniqKey(it, idx);
              const payNo = String(it?.PayNo || '').trim();
              const emailing = busyEmail === payNo;
              const downloading = busyDownload === payNo;
              return (
                <div key={key} className="p-4 space-y-3">
                  {/* Header row with checkbox and pay info */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="h-5 w-5 mt-1"
                      checked={!!checked[key]}
                      onChange={(e: any) => setChecked(s => ({ ...s, [key]: e.target.checked }))}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`font-mono ${TEXT.strong}`}>{it.PayNo || '—'}</span>
                        <span className="text-lg font-semibold text-green-700">
                          ${it.TotalNet != null ? Number(it.TotalNet).toFixed(2) : '—'}
                        </span>
                      </div>
                      <div className={`mt-1 ${TEXT.bodyMuted}`}>
                        {it.PaymentDate ? new Date(it.PaymentDate).toLocaleDateString('en-AU', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        }) : '—'}
                      </div>
                      <div className={`mt-1 ${TEXT.caption}`}>
                        Period: {it.PaySummaryStartDate
                          ? new Date(it.PaySummaryStartDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
                          : '—'} → {it.PaySummaryEndDate
                          ? new Date(it.PaySummaryEndDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
                          : '—'}
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 pl-8">
                    <Button variant="outline" size="sm" onClick={() => doPreview(it)} title="Preview" className="flex-1">
                      <EyeIcon className="h-4 w-4" />
                      <span>View</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => doDownload(it)} disabled={downloading} title="Download PDF" className="flex-1">
                      <ArrowDownTrayIcon className={`h-4 w-4 ${downloading ? 'animate-pulse' : ''}`} />
                      <span>{downloading ? '...' : 'PDF'}</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => doEmail(it)} disabled={emailing} title="Email me" className="flex-1">
                      <EnvelopeIcon className={`h-4 w-4 ${emailing ? 'animate-pulse' : ''}`} />
                      <span>{emailing ? '...' : 'Email'}</span>
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Lazy-load older pays — visible whenever the cache has more
              history available behind what we've already pulled in. */}
          {hasOlder && (
            <div className="mt-4 flex justify-center">
              <Button variant="outline" type="button" onClick={loadOlder} disabled={loadingOlder}>
                {loadingOlder ? 'Loading older payslips…' : 'Load older payslips'}
              </Button>
            </div>
          )}
          </div>
        </div>
      )}

      {/* Toast - uses safe area for Android navigation buttons */}
      {toast && (
        <div
          className="fixed bottom-safe-4 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-full px-4 py-2 shadow-md z-50"
          onAnimationEnd={() => setTimeout(() => setToast(''), 2200)}
        >
          {toast}
        </div>
      )}

      {/* Preview Modal */}
      {preview && (
        <Modal
          isOpen
          onClose={() => setPreview(null)}
          title={`Payslip — ${preview.PayNo || '—'}`}
          size="lg"
        >
          <div className="p-4 sm:p-5 overflow-y-auto flex-1">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 text-gray-900">
              <div className="col-span-2 sm:col-span-1">
                <div className={`sm:text-sm ${TEXT.caption}`}>Company</div>
                <div className={`sm:text-base ${TEXT.bodyMedium}`}>{preview.CompanyName || '—'}</div>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <div className={`sm:text-sm ${TEXT.caption}`}>Pay No</div>
                <div className={`font-mono sm:text-base ${TEXT.bodyMedium}`}>{preview.PayNo || '—'}</div>
              </div>
              <div>
                <div className={`sm:text-sm ${TEXT.caption}`}>Payment Date</div>
                <div className={`sm:text-base ${TEXT.bodyMedium}`}>
                  {preview.PaymentDate ? new Date(preview.PaymentDate).toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  }) : '—'}
                </div>
              </div>
              <div>
                <div className={`sm:text-sm ${TEXT.caption}`}>Pay Period</div>
                <div className={`sm:text-base ${TEXT.bodyMedium}`}>
                  {(preview.PaySummaryStartDate
                    ? new Date(preview.PaySummaryStartDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
                    : '—') +
                    ' → ' +
                    (preview.PaySummaryEndDate
                      ? new Date(preview.PaySummaryEndDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
                      : '—')}
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <div className={`sm:text-sm ${TEXT.caption}`}>Total Net</div>
                <div className="font-semibold text-lg sm:text-xl text-green-700">
                  ${preview.TotalNet != null ? Number(preview.TotalNet).toFixed(2) : '—'}
                </div>
              </div>
              {preview.TotalDeductions != null && Number(preview.TotalDeductions) > 0 && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className={`sm:text-sm ${TEXT.caption}`}>Total Deductions</div>
                  <div className={`sm:text-base ${TEXT.bodyMedium}`}>
                    ${Number(preview.TotalDeductions).toFixed(2)}
                  </div>
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className={`sm:text-sm ${TEXT.caption}`}>Total Tax</div>
                <div className={`sm:text-base ${TEXT.bodyMedium}`}>
                  ${preview.TotalTax != null ? Number(preview.TotalTax).toFixed(2) : '—'}
                </div>
              </div>
            </div>
          </div>
          <div className="px-4 sm:px-5 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => doDownload(preview)}>
              <ArrowDownTrayIcon className="h-5 w-5" /> Download PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => doEmail(preview)}>
              <EnvelopeIcon className="h-5 w-5" /> Email me
            </Button>
            <Button variant="outline" onClick={() => setPreview(null)}>
              Close
            </Button>
          </div>
        </Modal>
      )}

      {/* ===== Admin modal: Search by code or name ===== */}
      {canAdmin && showAdmin && (
        <Modal
          isOpen
          onClose={() => setShowAdmin(false)}
          title={
            <span className="flex items-center gap-2">
              <UserGroupIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-700" aria-hidden />
              Admin · Find Staff Payslips
            </span>
          }
          size="2xl"
          zIndexClass="z-[60]"
        >
          <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-4 overflow-y-auto flex-1">
            {/* Search row */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by Code or Name…"
                  value={adminQuery}
                  onChange={(e: any) => setAdminQuery(e.target.value)}
                  onKeyDown={(e: any) => e.key === 'Enter' && adminSearch()}
                  className={`${FIELD_INPUT_CLASS} pl-10`}
                />
              </div>
              <Button variant="outline" size="sm" onClick={adminSearch} disabled={adminSearching || !adminQuery.trim()}>
                {adminSearching ? 'Searching…' : 'Search'}
              </Button>
            </div>

            {/* Results - Mobile card view */}
            {adminResults.length > 0 && (
              <>
                {/* Desktop table */}
                <div className="hidden sm:block border border-gray-200 rounded-lg overflow-hidden">
                  <Table density="compact" scroll={false} className="min-w-full">
                    <THead>
                      <TR>
                        <TH>Person Code</TH>
                        <TH>Name</TH>
                        <TH>Email</TH>
                        <TH>Action</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {adminResults.map(u => (
                        <TR key={u.personCode}>
                          <TD className="font-mono">{u.personCode}</TD>
                          <TD>{u.name || '—'}</TD>
                          <TD>{u.email || '—'}</TD>
                          <TD>
                            <Button variant="outline" size="xs" onClick={() => adminLoad(u.personCode)}>
                              Load payslips
                            </Button>
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                </div>
                {/* Mobile cards */}
                <div className="sm:hidden space-y-2">
                  {adminResults.map(u => (
                    <div key={u.personCode} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-mono text-sm">{u.personCode}</div>
                        <div className={`truncate ${TEXT.strong}`}>{u.name || '—'}</div>
                        <div className={`truncate ${TEXT.caption}`}>{u.email || '—'}</div>
                      </div>
                      <Button variant="outline" size="xs" onClick={() => adminLoad(u.personCode)} className="shrink-0">
                        Load
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Loaded items for selected user */}
            {adminSelected && (
              <div className="space-y-3">
                <div className={`bg-blue-50 rounded-lg p-3 ${TEXT.body}`}>
                  <div className={TEXT.strong}>{adminSelected.name}</div>
                  <div className={TEXT.caption}>
                    {adminSelected.personCode}
                    {adminSelected.email && ` · ${adminSelected.email}`}
                  </div>
                </div>

                {/* Desktop table */}
                <div className="hidden md:block border border-gray-200 rounded-lg overflow-x-auto">
                  <Table density="compact" scroll={false} className="min-w-full">
                    <THead>
                      <TR>
                        <TH>Pay No</TH>
                        <TH>Payment Date</TH>
                        <TH align="right">Total Net</TH>
                        <TH>Pay Period</TH>
                        <TH>Actions</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {adminItems.map((it, idx) => {
                        const payNo = String(it?.PayNo || '').trim();
                        const emailing = adminBusyEmail === payNo;
                        const downloading = adminBusyDownload === payNo;
                        return (
                          <TR key={(it.PayNo || '') + '_' + idx}>
                            <TD className="font-mono">{it.PayNo}</TD>
                            <TD>
                              {it.PaymentDate
                                ? new Date(it.PaymentDate).toLocaleDateString()
                                : '—'}
                            </TD>
                            <TD align="right">
                              {it.TotalNet != null ? Number(it.TotalNet).toFixed(2) : '—'}
                            </TD>
                            <TD>
                              {(it.PaySummaryStartDate
                                ? new Date(it.PaySummaryStartDate).toLocaleDateString()
                                : '—') +
                                ' → ' +
                                (it.PaySummaryEndDate
                                  ? new Date(it.PaySummaryEndDate).toLocaleDateString()
                                  : '—')}
                            </TD>
                            <TD>
                              <div className="flex flex-wrap gap-2">
                                <Button variant="outline" size="xs" onClick={() => adminDownload(adminSelected.personCode, it)} disabled={downloading}>
                                  <ArrowDownTrayIcon
                                    className={`h-5 w-5 ${downloading ? 'animate-pulse' : ''}`}
                                  />
                                  {downloading ? '...' : 'Download'}
                                </Button>
                                <Button variant="outline" size="xs" onClick={() => adminEmail(adminSelected.personCode, it)} disabled={emailing}>
                                  <EnvelopeIcon
                                    className={`h-5 w-5 ${emailing ? 'animate-pulse' : ''}`}
                                  />
                                  {emailing ? '...' : 'Email'}
                                </Button>
                              </div>
                            </TD>
                          </TR>
                        );
                      })}
                    </TBody>
                  </Table>
                </div>

                {/* Mobile cards for admin items */}
                <div className="md:hidden space-y-2">
                  {adminItems.map((it, idx) => {
                    const payNo = String(it?.PayNo || '').trim();
                    const emailing = adminBusyEmail === payNo;
                    const downloading = adminBusyDownload === payNo;
                    return (
                      <div key={(it.PayNo || '') + '_' + idx} className="border border-gray-200 rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-sm">{it.PayNo}</span>
                          <span className="text-lg font-semibold text-green-700">
                            ${it.TotalNet != null ? Number(it.TotalNet).toFixed(2) : '—'}
                          </span>
                        </div>
                        <div className={TEXT.bodyMuted}>
                          {it.PaymentDate ? new Date(it.PaymentDate).toLocaleDateString('en-AU', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          }) : '—'}
                        </div>
                        <div className={TEXT.caption}>
                          Period: {it.PaySummaryStartDate
                            ? new Date(it.PaySummaryStartDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
                            : '—'} → {it.PaySummaryEndDate
                            ? new Date(it.PaySummaryEndDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
                            : '—'}
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button variant="outline" size="sm" onClick={() => adminDownload(adminSelected.personCode, it)} disabled={downloading} className="flex-1">
                            <ArrowDownTrayIcon className={`h-4 w-4 ${downloading ? 'animate-pulse' : ''}`} />
                            {downloading ? '...' : 'PDF'}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => adminEmail(adminSelected.personCode, it)} disabled={emailing} className="flex-1">
                            <EnvelopeIcon className={`h-4 w-4 ${emailing ? 'animate-pulse' : ''}`} />
                            {emailing ? '...' : 'Email'}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <ModalFooter>
            <Button variant="outline" onClick={() => setShowAdmin(false)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </PageLayout>
  );
}
